import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { EventSourceParserStream } from 'eventsource-parser/stream';

// ---- Types ----

export interface ChatMessage {
  payload: string;
  type: 'user' | 'agent' | 'error';
  tools: Array<{ name: string; input: string }>;
}

export interface SessionInfo {
  sessionId: string;
  agent: string;
  created: string;
  lastActivity: string;
  title: string;
}

interface ChatEvent {
  type: 'ChunkEvent' | 'ResponseEvent' | 'ToolEvent' | 'ErrorEvent';
  token?: string;
  sessionId?: string;
  name?: string;
  input?: string;
  message?: string;
}

// ---- Hook ----

export function useSessionHistory({ agentName }: { agentName: string }) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Mutable refs to avoid stale closures in streaming and async callbacks.
  // These are the source of truth inside long-running async operations;
  // state setters are only used to trigger re-renders.
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;

  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = activeSessionId;

  const agentNameRef = useRef(agentName);
  agentNameRef.current = agentName;

  // ---- API helpers ----

  const getBaseUrl = useCallback(async () => {
    return discoveryApi.getBaseUrl('aws-genai');
  }, [discoveryApi]);

  const fetchSessionsForAgent = useCallback(
    async (agent: string) => {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(
        `${baseUrl}/v1/sessions/${encodeURIComponent(agent)}`,
      );
      if (!response.ok) return [];
      return (await response.json()) as SessionInfo[];
    },
    [getBaseUrl, fetchApi],
  );

  const fetchMessagesForSession = useCallback(
    async (agent: string, sessionId: string): Promise<ChatMessage[]> => {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(
        `${baseUrl}/v1/sessions/${encodeURIComponent(agent)}/${encodeURIComponent(sessionId)}/messages`,
      );
      if (!response.ok) return [];
      return (await response.json()) as ChatMessage[];
    },
    [getBaseUrl, fetchApi],
  );

  // ---- Load sessions on mount ----
  // Because the parent keys on agentName, this effect runs exactly once
  // per agent — on mount. Unmount aborts any in-flight streaming via the
  // cleanup, so there is no cross-agent state leakage.

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const list = await fetchSessionsForAgent(agentName);
      if (cancelled) return;
      setSessions(list);
      if (list.length > 0) {
        const latest = list[0];
        setActiveSessionId(latest.sessionId);
        const msgs = await fetchMessagesForSession(agentName, latest.sessionId);
        if (!cancelled) setMessages(msgs);
      }
      setIsInitializing(false);
    })();

    return () => {
      cancelled = true;
      // Abort any in-flight streaming when the component unmounts
      // (i.e. when the user switches agents). The server will still
      // save the partial response to the LangGraph checkpoint via
      // its own request.on('close') handler.
      abortControllerRef.current?.abort();
    };
  }, [agentName, fetchSessionsForAgent, fetchMessagesForSession]);

  // ---- Switch to an existing session ----

  const switchSession = useCallback(
    async (sessionId: string) => {
      if (sessionId === sessionIdRef.current) return;
      setActiveSessionId(sessionId);
      setIsLoadingSession(true);
      const msgs = await fetchMessagesForSession(agentNameRef.current, sessionId);
      // Guard: only apply if we're still on the same session
      if (sessionIdRef.current === sessionId) {
        setMessages(msgs);
        setIsLoadingSession(false);
      }
    },
    [fetchMessagesForSession],
  );

  // ---- Start a new conversation ----

  const startNewConversation = useCallback(() => {
    setActiveSessionId(null);
    setMessages([]);
    messagesRef.current = [];
  }, []);

  // ---- Send a message (streaming) ----

  const sendMessage = useCallback(
    async (userMessage: string) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);
      const sendAgent = agentNameRef.current;

      // Append user message + empty agent placeholder
      const userMsg: ChatMessage = {
        payload: userMessage,
        type: 'user',
        tools: [],
      };
      const agentMsg: ChatMessage = { payload: '', type: 'agent', tools: [] };
      const updated = [...messagesRef.current, userMsg, agentMsg];
      setMessages(updated);
      messagesRef.current = updated;

      try {
        const baseUrl = await getBaseUrl();
        const response = await fetchApi.fetch(`${baseUrl}/v1/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userMessage,
            sessionId: sessionIdRef.current ?? undefined,
            agentName: sendAgent,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Chat request failed: ${response.statusText}`);
        }

        const reader = response
          .body!.pipeThrough(new TextDecoderStream())
          .pipeThrough(new EventSourceParserStream())
          .pipeThrough(
            new TransformStream<any, ChatEvent>({
              transform(parsedEvent, ctrl) {
                try {
                  const data = JSON.parse(parsedEvent.data);
                  ctrl.enqueue(data as ChatEvent);
                } catch {
                  // skip unparseable events
                }
              },
            }),
          )
          .getReader();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const current = [...messagesRef.current];
          const lastIdx = current.length - 1;
          const last = { ...current[lastIdx] };

          if (value.type === 'ChunkEvent' && value.token) {
            last.payload = last.payload + value.token;
          } else if (value.type === 'ResponseEvent' && value.sessionId) {
            if (!sessionIdRef.current) {
              setActiveSessionId(value.sessionId);
            }
          } else if (value.type === 'ToolEvent') {
            last.tools = [
              ...last.tools,
              { name: value.name ?? '', input: value.input ?? '' },
            ];
          } else if (value.type === 'ErrorEvent') {
            last.payload = last.payload + `Error: ${value.message ?? 'Unknown error'}`;
            last.type = 'error';
          }

          current[lastIdx] = last;
          setMessages(current);
          messagesRef.current = current;
        }
      } catch (e: any) {
        if (e.name !== 'AbortError' && messagesRef.current.length > 0) {
          const current = [...messagesRef.current];
          const lastIdx = current.length - 1;
          current[lastIdx] = {
            ...current[lastIdx],
            payload:
              current[lastIdx].payload + `Error: ${e.message ?? 'Unknown error'}`,
            type: 'error',
          };
          setMessages(current);
          messagesRef.current = current;
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
        // Refresh session list (new session may have been created)
        const list = await fetchSessionsForAgent(agentNameRef.current);
        setSessions(list);
      }
    },
    [getBaseUrl, fetchApi, fetchSessionsForAgent],
  );

  // ---- Cancel streaming ----

  const cancelStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
  }, []);

  // ---- Clear / end session ----

  const clearSession = useCallback(async () => {
    const currentAgent = agentNameRef.current;
    const currentSession = sessionIdRef.current;
    if (currentSession) {
      const baseUrl = await getBaseUrl();
      await fetchApi.fetch(`${baseUrl}/v1/endSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentName: currentAgent,
          sessionId: currentSession,
        }),
      });
    }
    setMessages([]);
    messagesRef.current = [];
    setActiveSessionId(null);
    setIsLoading(false);
    // Refresh session list
    const list = await fetchSessionsForAgent(currentAgent);
    if (agentNameRef.current === currentAgent) {
      setSessions(list);
    }
  }, [getBaseUrl, fetchApi, fetchSessionsForAgent]);

  return {
    sessions,
    activeSessionId,
    messages,
    isLoading,
    isLoadingSession,
    isInitializing,
    sendMessage,
    switchSession,
    startNewConversation,
    clearSession,
    cancelStreaming,
  };
}
